import React from "react";
import { useNavigate } from "react-router";
import Toaster from "../../components/common/toaster";
import { useFormik } from "formik";
import * as Yup from "yup";
import { setLoggedIn } from "../../store/slice/auth";
import { useDispatch } from "react-redux";
import { toastSuccess } from "../../common/toastNotification";
import { useTranslation } from "react-i18next";
import { CommonTextField } from "../../components/widgets/common_textField";
import CommonButton from "../../components/widgets/common_button";
import { AppImages } from "../../common/ImagePath";
import AuthService from "../../service/auth.service";
import logoIconPng from "../../assets/JOBO.png";

const Login = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const initialValues = {
    email: "",
    password: "",
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t("messages.email"))
      .required(t("messages.emailRequired")),
    password: Yup.string()
      .min(6, t("messages.password"))
      .required(t("messages.passwordRequired")),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const response = await AuthService.login({
        email: values.email,
        password: values.password,
      });

      // Check if login was successful
      if (response && response.data && response.data.statusCode === 200) {
        localStorage.setItem("isLoggedIn", "true");
        dispatch(setLoggedIn(true));

        if (values.rememberMe) {
          localStorage.setItem("rememberedEmail", values.email);
        }

        toastSuccess(t("messages.loginSuccess"));
        navigate("/users");
      } else {
        Toaster("error", "Login failed - Invalid response format");
      }
    } catch (error) {
      Toaster(
        "error",
        error?.response?.data?.message || t("messages.somethingWentWrong")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <div className="flex items-center justify-center h-screen w-full">
      <div className="max-w-3xl w-full px-5">
        <div className="flex min-h-full flex-col justify-center px-6 py-12 lg:px-8 w-full border-input border h-fit rounded-md shadow-lg">
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <a href="/" className="flex items-center gap-2 justify-center">
              <div className="w-26 h-12">
                <img
                  src={logoIconPng}
                  alt="logo"
                  className="block dark:hidden w-full h-full object-contain"
                />
                <img
                  src={logoIconPng}
                  alt="logoDark"
                  className="hidden dark:block w-full h-full object-contain"
                />
              </div>
            </a>
            <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight dark:text-white text-gray-900">
              {t("auth.signIn")}
            </h2>
          </div>

          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <form className="mt-10 space-y-6" onSubmit={formik.handleSubmit}>
              <div className="grid gap-1.5">
                <CommonTextField
                  label={t("email")}
                  type="email"
                  name="email"
                  placeholder={t("emailPlaceholder")}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && formik.errors.email}
                />
              </div>

              <div className="grid gap-1.5">
                <CommonTextField
                  label={t("password")}
                  type="password"
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder={t("passwordPlaceholder")}
                  isPassword
                  error={formik.touched.password && formik.errors.password}
                />
              </div>

              <CommonButton
                type="submit"
                className="w-full"
                isLoading={formik.isSubmitting}
              >
                {t("auth.signIn")}
              </CommonButton>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
